/**
 * ----- Definition des routes de l'application -----
 */

import React, { lazy, Suspense } from 'react';
import {createBrowserRouter, Outlet} from 'react-router-dom';

const ScrollToTop = lazy(() => import('../tsx/components/navigation/ScrollToTop'));
const Centered = lazy(() => import('../tsx/templates/Centered'));
const CenteredOnLeft = lazy(() => import('../tsx/templates/CenteredOnLeft'));
const Dashboard = lazy(() => import('../tsx/templates/Dashboard'));

const ContentsLoading = lazy(() => import('../tsx/contents/ContentsLoading'));
const ContentsError = lazy(() => import('../tsx/contents/ContentsError'));
const ContentsLogin = lazy(() => import('../tsx/contents/login/ContentsLogin'));
const ContentsResetPassword = lazy(() => import('../tsx/contents/reset-password/OsacResetPasswordForm'));

const ContentsDefault = lazy(() => import('../tsx/contents/ContentsDefault'));
const ContentsPlanDeCharge = lazy(() => import('../tsx/contents/plan-de-charge/ContentsPlanDeCharge'));

const ContentsRefActivityTypes = lazy(() => import('../tsx/contents/referentiels/activityTypes/ContentsRefActivityTypes'));
const ContentsRefTaskTypes = lazy(() => import('../tsx/contents/referentiels/taskTypes/ContentsRefTaskTypes'));
const ContentsRefSkills = lazy(() => import('../tsx/contents/referentiels/skills/ContentsRefSkills'));
const ContentsRefBillingCustomers = lazy(() => import('../tsx/contents/referentiels/billingCustomers/ContentsRefBillingCustomers'));
const ContentsImportBillingCustomers = lazy(() => import('../tsx/contents/referentiels/billingCustomers/ContentsImportBillingCustomers'));

const ContentsListUsers = lazy(() => import('../tsx/contents/users/ContentsListUsers'));
const ContentsManageUser = lazy(() => import('../tsx/contents/users/ContentsManageUser'));

const ContentsListCollaborators = lazy(() => import('../tsx/contents/collaborators/ContentsListCollaborators'));
const ContentsManageCollaborator = lazy(() => import('../tsx/contents/collaborators/ContentsManageCollaborator'));
const ContentsDetailsCollaborator = lazy(() => import('../tsx/contents/collaborators/ContentsDetailsCollaborator'));
const ContentsImportCollaborators = lazy(() => import('../tsx/contents/collaborators/ContentsImportCollaborators'));
const ContentsListOOODaysCollaborators = lazy(() => import('../tsx/contents/collaborators/ContentsListOOODaysCollaborators'));

const ContentsListCustomers = lazy(() => import('../tsx/contents/customers').then(m => ({ default: m.ContentsListCustomers })));
const ContentsManageCustomer = lazy(() => import('../tsx/contents/customers').then(m => ({ default: m.ContentsManageCustomer })));
const ContentsDetailsCustomer = lazy(() => import('../tsx/contents/customers').then(m => ({ default: m.ContentsDetailsCustomer })));
const ContentsImportCustomers = lazy(() => import('../tsx/contents/customers').then(m => ({ default: m.ContentsImportCustomers })));

const ContentsListCharges = lazy(() => import('../tsx/contents/charges').then(m => ({ default: m.ContentsListCharges })));
const ContentsManageCharge = lazy(() => import('../tsx/contents/charges').then(m => ({ default: m.ContentsManageCharge })));
const ContentsDetailsCharge = lazy(() => import('../tsx/contents/charges').then(m => ({ default: m.ContentsDetailsCharge })));
const ContentsChargesReport = lazy(() => import('../tsx/contents/charges').then(m => ({ default: m.ContentsChargesReport })));
const ContentsImportCharges = lazy(() => import('../tsx/contents/charges').then(m => ({ default: m.ContentsImportCharges })));

const ContentsListActivities = lazy(() => import('../tsx/contents/charges').then(m => ({ default: m.ContentsListActivities })));
const ContentsManageActivity = lazy(() => import('../tsx/contents/charges').then(m => ({ default: m.ContentsManageActivity })));
const ContentsDetailsActivity = lazy(() => import('../tsx/contents/charges').then(m => ({ default: m.ContentsDetailsActivity })));

const ContentsListModelCharges = lazy(() => import('../tsx/contents/charges').then(m => ({ default: m.ContentsListModelCharges })));
const ContentsManageModelCharge = lazy(() => import('../tsx/contents/charges').then(m => ({ default: m.ContentsManageModelCharge })));
const ContentsDetailsChargeModel = lazy(() => import('../tsx/contents/charges').then(m => ({ default: m.ContentsDetailsChargeModel })));

const ContentsFilters = lazy(() => import('../tsx/contents/filters/ContentsFilters'));
const ContentsAlerts = lazy(() => import('../tsx/contents/alerts').then(m => ({ default: m.ContentsAlerts })));
const ContentsConfigAlerts = lazy(() => import('../tsx/contents/alerts').then(m => ({ default: m.ContentsConfigAlerts })));

// styleguide
const ContentsStyleguide = lazy(() => import('../tsx/contents/_styleguide'));
const ContentsSGAlerts = lazy(() => import('../tsx/contents/_styleguide/ContentsAlerts'));
const ContentsSGKeyCards = lazy(() => import('../tsx/contents/_styleguide/ContentsKeyCards'));
const ContentsSGButtons = lazy(() => import('../tsx/contents/_styleguide/ContentsButtons'));
const ContentsSGTables = lazy(() => import('../tsx/contents/_styleguide/ContentsTables'));
const ContentsSGProgress = lazy(() => import('../tsx/contents/_styleguide/ContentsProgress'));
const ContentsSGCharts = lazy(() => import('../tsx/contents/_styleguide/ContentsCharts'));
const ContentsSGTimelines = lazy(() => import('../tsx/contents/_styleguide/ContentsTimelines'));
const ContentsSGCards = lazy(() => import('../tsx/contents/_styleguide/ContentsCards'));
const ContentsSGAvatars = lazy(() => import('../tsx/contents/_styleguide/ContentsAvatars'));
const ContentsSGFields = lazy(() => import('../tsx/contents/_styleguide/ContentsFields'));

/** Composant affichant un loader centré */
const loading = (
  <div className="min-h-screen w-full">
    <Centered>
      <ContentsLoading />
    </Centered>
  </div>
);

/** Ensemble de routes de l'application, quelles que soient les restrictions d'accès */
const routeLogin = {
  //index: true,
  path: '/',
  element: (
    <Suspense fallback={loading}>
      <div className="min-h-screen w-full">
        <ScrollToTop />
        <CenteredOnLeft>
          <Outlet />
        </CenteredOnLeft>
      </div>
    </Suspense>
  ),
  errorElement: (
    <Suspense fallback={loading}>
      <div className="min-h-screen w-full">
        <ScrollToTop />
        <CenteredOnLeft>
          <ContentsError />
        </CenteredOnLeft>
      </div>
    </Suspense>
  ),
  children: [
    {
      path: 'login',
      element: <ContentsLogin />
    },
    {
      path: 'reset-password',
      element:  <ContentsResetPassword />
    },
  ]
};

const routeRacine = {
  path: '/',
  errorElement: (
    <Suspense fallback={loading}>
      <div className="min-h-screen w-full">
        <Centered>
          <ContentsError />
        </Centered>
      </div>
    </Suspense>
  ),
  element: (
      <Suspense fallback={loading}>
        <div className="min-h-screen w-full">
          <ScrollToTop />
          <Dashboard />
        </div>
      </Suspense>
  ),
  children: [
    {
      path: '',
      element: <ContentsDefault />,
    }
  ],
};
const routesRacineBase = [
  // {
  //   index: true,
  //   path: '/home',
  //   element: <ContentsHome />,
  // },
  {
    path: '/plan-de-charge',
    element: <ContentsPlanDeCharge />,
  },
  {
    path: '/referentiel-activites',
    element: <ContentsRefActivityTypes />,
  },
  {
    path: '/referentiel-taches',
    element: <ContentsRefTaskTypes />,
  },
  // {
  //   path: '/referentiel-competences',
  //   element: <ContentsRefSkills />,
  // },
  {
    path: '/referentiel-donneurs-d-ordre',
    element: <ContentsRefBillingCustomers />,
  },
  {
    path: '/importer-donneurs-d-ordre',
    element: <ContentsImportBillingCustomers />,
  },
  {
    path: '/utilisateurs',
    element: <ContentsListUsers />,
  },
  {
    path: '/creer-utilisateur',
    element: <ContentsManageUser />,
  },
  {
    path: '/utilisateurs/:id',
    element: <ContentsManageUser />,
  },
  {
    path: '/collaborateurs',
    element: <ContentsListCollaborators />,
  },
  {
    path: '/creer-collaborateur',
    element: <ContentsManageCollaborator />,
  },
  {
    path: '/collaborateurs/:id/modifier',
    element: <ContentsManageCollaborator />,
  },
  {
    path: '/collaborateurs/:id/details',
    element: <ContentsDetailsCollaborator />,
  },
  {
    path: '/collaborateurs/importer',
    element: <ContentsImportCollaborators />,
  },
  {
    path: '/collaborateurs/stats-conges',
    element: <ContentsListOOODaysCollaborators />,
  },
  {
    path: '/organismes',
    element: <ContentsListCustomers />,
  },
  {
    path: '/creer-organisme',
    element: <ContentsManageCustomer />,
  },
  {
    path: '/organismes/:id/modifier',
    element: <ContentsManageCustomer />,
  },
  {
    path: '/organismes/:id/details',
    element: <ContentsDetailsCustomer />,
  },
  {
    path: '/organismes/importer',
    element: <ContentsImportCustomers />,
  },
  {
    path: '/charges',
    element: <ContentsListCharges />,
  },
  {
    path: '/creer-charge',
    element: <ContentsManageCharge />,
  },
  {
    path: '/creer-charge-depuis-model',
    element: <ContentsManageCharge withModel={true} />,
  },
  {
    path: '/charges-report',
    element: <ContentsChargesReport />,
  },
  {
    path: '/charges/:id/modifier',
    element: <ContentsManageCharge />,
  },
  {
    path: '/charges/:id/details',
    element: <ContentsDetailsCharge />,
  },
  {
    path: '/importer-plan-de-charges',
    element: <ContentsImportCharges />,
  },
  {
    path: '/model-charges',
    element: <ContentsListModelCharges />,
  },
  {
    path: '/creer-model-charge',
    element: <ContentsManageModelCharge />,
  },
  {
    path: '/model-charges/:id/modifier',
    element: <ContentsManageModelCharge />,
  },
  {
    path: '/model-charges/:id/details',
    element: <ContentsDetailsChargeModel />,
  },
  {
    path: '/activites',
    element: <ContentsListActivities />,
  },
  {
    path: '/activites/:id/modifier',
    element: <ContentsManageActivity />,
  },
  {
    path: '/activites/:id/details',
    element: <ContentsDetailsActivity />,
  },
  {
    path: '/filters',
    element: <ContentsFilters />,
  },
  {
    index: true,
    path: '/alertes',
    element: <ContentsAlerts />,
  },
  {
    path: '/config-alertes',
    element: <ContentsConfigAlerts />,
  },
];
// si skill activées, on ajoute la route
if (process.env.REACT_APP_SKILLS_ENABLED === 'true') {
  routesRacineBase.push({
    path: '/referentiel-competences',
    element: <ContentsRefSkills />,
  });
}
// on ajoute les pages de base du dashboard
routesRacineBase.forEach(r => routeRacine.children.push(r));

// en mode DEV, on ajoute les routes styleguide
if (process.env.REACT_APP_ENV !== 'production') {
  const routesRacineStyleguide = [
    {
      path: '/styleguide',
      element: <ContentsStyleguide />,
    },
    {
      path: '/styleguide/alerts',
      element: <ContentsSGAlerts />,
    },
    {
      path: '/styleguide/keycards',
      element: <ContentsSGKeyCards />,
    },
    {
      path: '/styleguide/buttons',
      element: <ContentsSGButtons />,
    },
    {
      path: '/styleguide/tables',
      element: <ContentsSGTables />,
    },
    {
      path: '/styleguide/progress',
      element: <ContentsSGProgress />,
    },
    {
      path: '/styleguide/charts',
      element: <ContentsSGCharts />,
    },
    {
      path: '/styleguide/timelines',
      element: <ContentsSGTimelines />,
    },
    {
      path: '/styleguide/cards',
      element: <ContentsSGCards />,
    },
    {
      path: '/styleguide/avatars',
      element: <ContentsSGAvatars />,
    },
    {
      path: '/styleguide/fields',
      element: <ContentsSGFields />,
    },
  ];
  routesRacineStyleguide.forEach(r => routeRacine.children.push(r));
}

const allRoutes = [
  routeLogin,
	routeRacine
];

const router = createBrowserRouter(allRoutes);
export default router;
